import React, { useState, useRef, Fragment } from 'react';
import _ from 'lodash';
import { Row, Col, Input, Form, FormGroup, Button } from 'reactstrap';
import { Query, withApollo } from 'react-apollo';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { DateRangePicker, SingleDatePicker } from 'react-dates';
import { CSVLink } from 'react-csv';
import 'react-dates/lib/css/_datepicker.css';
import Loading from '../Loading/Loading';

/**
 * @typedef {Object} FilterConfig
 * @property {('text'|'dateRange'|'singleDate'|'dropdown')} type - The type of filter input
 * @property {string} name - Unique identifier for the filter
 * @property {string} [placeholder] - Placeholder text for text inputs
 * @property {number} [colSize] - Bootstrap column size (1-12)
 * @property {string} [startDateField] - Field name for start date (dateRange only)
 * @property {string} [endDateField] - Field name for end date (dateRange only)
 * @property {string} [dateField] - Field name for single date (singleDate only)
 * @property {Array<{value: string, label: string}>} [options] - Options for dropdown filter
 * @property {Object} [defaultValues] - Default values for the filter
 * @property {boolean} [useDebounce] - Whether to debounce text input changes
 */

/**
 * @typedef {Object} SortConfig
 * @property {string} field - The field to sort by
 * @property {('asc'|'desc')} order - Sort direction
 */

/**
 * @typedef {Object} Column
 * @property {string} dataField - The field in the data object to display
 * @property {string} text - Column header text
 * @property {boolean} [sort] - Whether the column is sortable
 * @property {Object} [headerStyle] - CSS styles for the header
 * @property {Function} [formatter] - Function to format the cell value
 * @property {Function} [csvformatter] - Optional function to format the cell value specifically for CSV export
 */

/**
 * A reusable data table component that provides advanced data management features.
 *
 * Features:
 * - Remote pagination with configurable page sizes
 * - Remote sorting with customizable sort fields
 * - Dynamic filtering system supporting text search and date ranges
 * - GraphQL integration for data fetching
 * - Data transformation pipeline
 * - Customizable columns with formatting
 * - Debounced filter updates for better performance
 * - Responsive design with Bootstrap integration
 * - Optional CSV export functionality with customizable formatting
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.title - Title displayed at the top of the table and used in CSV export filename
 * @param {Object} props.query - GraphQL query document for fetching data
 * @param {Column[]} props.columns - Array of column configurations
 * @param {SortConfig} [props.defaultSort={ field: 'created', order: 'desc' }] - Default sorting configuration
 * @param {number} [props.defaultPageSize=10] - Default number of items per page
 * @param {number[]} [props.pageSizes=[10, 20, 50]] - Available page size options
 * @param {FilterConfig[]} [props.filters=[]] - Array of filter configurations
 * @param {Function} [props.buildWhereClause] - Function that builds GraphQL where clause from current filters
 * @param {Function} [props.transformData] - Function to transform response data before display
 * @param {string} props.queryName - Name of the query in the GraphQL response
 * @param {boolean} [props.enableExport=false] - Whether to enable CSV export functionality
 * @param {Function} [props.getHeaders] - Custom function to generate headers for CSV export
 * @param {Object} props.client - Apollo Client instance
 * @returns {React.ReactElement} Rendered QueryDataTable component
 *
 * @example
 * // Basic usage with minimal configuration
 * <QueryDataTable
 *   title="Users List"
 *   query={GET_USERS_QUERY}
 *   columns={[
 *     { dataField: 'id', text: 'ID', sort: true },
 *     { dataField: 'name', text: 'Name', sort: true },
 *     { dataField: 'email', text: 'Email' }
 *   ]}
 *   queryName="users"
 * />
 *
 * @example
 * // Advanced usage with filters, data transformation, and CSV export
 * <QueryDataTable
 *   title="Orders"
 *   query={GET_ORDERS_QUERY}
 *   columns={[
 *     {
 *       dataField: 'orderNumber',
 *       text: 'Order #',
 *       sort: true
 *     },
 *     {
 *       dataField: 'amount',
 *       text: 'Amount',
 *       formatter: (cell) => `$${cell.toFixed(2)}`,
 *       csvformatter: (cell) => cell.toFixed(2), // Different format for CSV
 *       headerStyle: { width: '150px' }
 *     }
 *   ]}
 *   filters={[
 *     {
 *       type: 'text',
 *       name: 'orderSearch',
 *       placeholder: 'Search orders...',
 *       useDebounce: true
 *     },
 *     {
 *       type: 'dateRange',
 *       name: 'orderDate',
 *       startDateField: 'startDate',
 *       endDateField: 'endDate'
 *     },
 *     {
 *       type: 'singleDate',
 *       name: 'deliveryDate',
 *       dateField: 'selectedDeliveryDate',
 *       placeholder: 'Select delivery date',
 *       colSize: 4,
 *       defaultValues: { date: moment() }
 *     },
 *     {
 *       type: 'dropdown',
 *       name: 'statusFilter',
 *       placeholder: 'Filter by status',
 *       colSize: 3,
 *       options: [
 *         { value: 'pending', label: 'Pending' },
 *         { value: 'completed', label: 'Completed' },
 *         { value: 'cancelled', label: 'Cancelled' }
 *       ],
 *       defaultValues: 'pending'
 *     }
 *   ]}
 *   buildWhereClause={(filters) => {
 *     const conditions = [];
 *     if (filters.orderSearch) {
 *       conditions.push(`orderNumber LIKE '%${filters.orderSearch}%'`);
 *     }
 *     if (filters.startDate && filters.endDate) {
 *       conditions.push(`orderDate BETWEEN '${filters.startDate}' AND '${filters.endDate}'`);
 *     }
 *     if (filters.selectedDeliveryDate) {
 *       conditions.push(`deliveryDate = '${filters.selectedDeliveryDate.format('YYYY-MM-DD')}'`);
 *     }
 *     if (filters.statusFilter) {
 *       conditions.push(`status = '${filters.statusFilter}'`);
 *     }
 *     return conditions.join(' AND ');
 *   }}
 *   transformData={(data) => data.map(order => ({
 *     ...order,
 *     amount: parseFloat(order.amount)
 *   }))}
 *   queryName="orders"
 *   defaultSort={{ field: 'orderDate', order: 'desc' }}
 *   defaultPageSize={20}
 *   pageSizes={[10, 20, 50, 100]}
 *   enableExport={true}
 * />
 */
const QueryDataTable = ({
  /** Title displayed at the top of the table */
  title,
  /** GraphQL query to fetch data */
  query,
  /** Array of column definitions */
  columns,
  /** Field to uniquely identify each row */
  keyField = 'id',
  /** Default sort configuration */
  defaultSort = { field: 'created', order: 'desc' },
  /** Default number of items per page */
  defaultPageSize = 10,
  /** Available page size options */
  pageSizes = [10, 20, 50],
  /** Array of filter configurations */
  filters = [],
  /** Function to build GraphQL where clause from current filters */
  buildWhereClause,
  /** Function to transform response data before display */
  transformData,
  /** Name of the query in the GraphQL response */
  queryName,
  /** Whether to enable CSV export functionality */
  enableExport = false,
  /** Custom function to generate headers for CSV export */
  getHeaders,
  /** Apollo Client instance */
  client,
}) => {
  // Initialize filter values with defaults from filter configurations
  const getInitialFilterValues = () => {
    const initialValues = {};
    filters.forEach((filter) => {
      if (filter.type === 'text' && filter.defaultValues) {
        initialValues[filter.name] = filter.defaultValues;
      } else if (filter.type === 'dateRange' && filter.defaultValues) {
        if (filter.startDateField && filter.defaultValues.startDate) {
          initialValues[filter.startDateField] = filter.defaultValues.startDate;
        }
        if (filter.endDateField && filter.defaultValues.endDate) {
          initialValues[filter.endDateField] = filter.defaultValues.endDate;
        }
      } else if (filter.type === 'singleDate' && filter.defaultValues && filter.dateField) {
        initialValues[filter.dateField] = filter.defaultValues.date;
      } else if (filter.type === 'dropdown' && filter.defaultValues) {
        initialValues[filter.name] = filter.defaultValues;
      }
    });
    return initialValues;
  };

  const [pagination, setPagination] = useState({ page: 1, size: defaultPageSize });
  const [sort, setSort] = useState(defaultSort);
  const [filterValues, setFilterValues] = useState(getInitialFilterValues());
  const [debouncedFilterValues, setDebouncedFilterValues] = useState({});
  const [focusedDateRange, setFocusedDateRange] = useState(null);
  const [focusedSingleDate, setFocusedSingleDate] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [exportData, setExportData] = useState(null);
  const csvLinkRef = useRef(null);

  // Debounce function to delay filter updates
  const debouncedUpdateFilters = useRef(
    _.debounce((newFilters) => {
      setFilterValues(newFilters);
    }, 500)
  ).current;

  /**
   * Handles changes to filter values
   * @param {string} name - Name of the filter
   * @param {*} value - New filter value
   */
  const handleFilterChange = (filterUpdates, useDebounce = false) => {
    const newFilters = { ...filterValues, ...filterUpdates };
    if (useDebounce) {
      setDebouncedFilterValues(newFilters);
      debouncedUpdateFilters(newFilters);
    } else {
      setFilterValues(newFilters);
    }
  };

  /**
   * Handles table changes (pagination, sorting)
   * @param {string} type - Type of change
   * @param {Object} params - Change parameters
   * @param {number} params.page - New page number
   * @param {number} params.sizePerPage - New page size
   * @param {string} [params.sortField] - Field to sort by
   * @param {string} [params.sortOrder] - Sort direction
   */
  const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    setPagination({ page, size: sizePerPage });
    if (sortField && sortOrder) {
      setSort({ field: sortField, order: sortOrder });
    }
  };

  /**
   * Renders a filter input based on its configuration
   * @param {FilterConfig} filter - Filter configuration
   * @returns {React.ReactElement} Filter input component
   */
  const renderFilter = (filter) => {
    switch (filter.type) {
      case 'text':
        return (
          <FormGroup key={filter.name}>
            <Input
              type='text'
              name={filter.name}
              id={filter.name}
              value={filter.useDebounce ? debouncedFilterValues[filter.name] || '' : filterValues[filter.name] || ''}
              onChange={(e) => handleFilterChange({ [filter.name]: e.target.value }, filter.useDebounce)}
              placeholder={filter.placeholder}
            />
          </FormGroup>
        );
      case 'dateRange':
        return (
          <FormGroup key={filter.name}>
            <DateRangePicker
              small
              showClearDates={true}
              startDate={filterValues[filter.startDateField]}
              startDateId={`${filter.startDateField}Id`}
              endDate={filterValues[filter.endDateField]}
              endDateId={`${filter.endDateField}Id`}
              onDatesChange={({ startDate, endDate }) => {
                if (!endDate || startDate.isSameOrBefore(endDate)) {
                  handleFilterChange({
                    [filter.startDateField]: startDate,
                    [filter.endDateField]: endDate,
                  });
                }
              }}
              focusedInput={focusedDateRange}
              onFocusChange={(focused) => setFocusedDateRange(focused)}
              isOutsideRange={() => false}
              displayFormat={'DD/MM/YYYY'}
            />
          </FormGroup>
        );
      case 'singleDate':
        return (
          <FormGroup key={filter.name}>
            <SingleDatePicker
              small
              showClearDate={true}
              date={filterValues[filter.dateField]}
              onDateChange={(date) => {
                handleFilterChange({
                  [filter.dateField]: date,
                });
              }}
              focused={focusedSingleDate[filter.name] || false}
              onFocusChange={({ focused }) => {
                setFocusedSingleDate((prev) => ({
                  ...prev,
                  [filter.name]: focused,
                }));
              }}
              id={`${filter.name}Id`}
              isOutsideRange={() => false}
              displayFormat={'DD/MM/YYYY'}
              placeholder={filter.placeholder || 'Select date'}
            />
          </FormGroup>
        );
      case 'dropdown':
        return (
          <FormGroup key={filter.name}>
            <Input
              type='select'
              name={filter.name}
              id={filter.name}
              value={filterValues[filter.name] || ''}
              onChange={(e) => handleFilterChange({ [filter.name]: e.target.value })}
            >
              <option value=''>{filter.placeholder || 'Select option'}</option>
              {filter.options &&
                filter.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </Input>
          </FormGroup>
        );
      default:
        return null;
    }
  };

  /**
   * Generates headers for CSV export based on column definitions
   * @returns {Array} Array of header objects for CSV export
   */
  const getDefaultHeaders = () => {
    return columns.map((col) => ({
      label: col.text,
      key: col.dataField,
    }));
  };

  /**
   * Generates filename for CSV export
   * @returns {string} Filename for CSV export
   */
  const handleFileName = () => {
    const date = new Date().toLocaleDateString();
    return `${title || 'export'}_${date}.csv`;
  };

  /**
   * Transforms an array of data objects into a new array formatted for CSV export.
   * For each column, applies a custom CSV formatter if provided, otherwise uses the raw value.
   *
   * @param {Array<Object>} data - The array of data objects to be transformed.
   * @returns {Array<Object>} The transformed array of objects, ready for CSV export.
   */
  const getCSVData = (data) => {
    return data.map((item) => {
      const csvItem = {};
      columns.forEach((col) => {
        if (col.csvformatter) {
          csvItem[col.dataField] = col.csvformatter(item[col.dataField]);
        } else {
          csvItem[col.dataField] = item[col.dataField];
        }
      });
      return csvItem;
    });
  };

  /**
   * Handles exporting data by fetching all elements from the server, transforming the data if needed,
   * and triggering a CSV download. Sets the exporting state during the process.
   *
   * @async
   * @param {number} totalElements - The total number of elements to export.
   * @returns {Promise<void>} Resolves when the export process is complete.
   */
  const handleExport = async (totalElements) => {
    setIsExporting(true);
    try {
      const { data } = await client.query({
        query,
        variables: {
          page: {
            sort: {
              orders: [
                {
                  property: sort.field,
                  direction: sort.order,
                },
              ],
            },
            pageSize: totalElements,
            pageNumber: 0,
          },
          spec: {
            where: buildWhereClause ? buildWhereClause(filterValues) : null,
          },
        },
      });

      if (data && Array.isArray(data[queryName].content)) {
        const allContent = data[queryName].content;
        const transformedAllData = transformData ? transformData(allContent) : allContent;
        setExportData(transformedAllData);
        // Trigger download automatically
        if (csvLinkRef.current) {
          csvLinkRef.current.link.click();
        }
      }
    } catch (error) {
      console.error('Error fetching data for export:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className='manage-users'>
      <Row className='subheader'>
        <Col className='col-8'>
          <h3>{title}</h3>
        </Col>
      </Row>
      <div className='page-cnt'>
        {filters.length > 0 && (
          <Form>
            <Row className='mb-3'>
              {filters.map((filter) => (
                <Col key={filter.name} md={filter.colSize || 12 / filters.length}>
                  {renderFilter(filter)}
                </Col>
              ))}
            </Row>
          </Form>
        )}
        <Row>
          <Col sm='12'>
            <Query
              query={query}
              variables={{
                page: {
                  sort: {
                    orders: [
                      {
                        property: sort.field,
                        direction: sort.order,
                      },
                    ],
                  },
                  pageSize: pagination.size,
                  pageNumber: pagination.page - 1,
                },
                spec: {
                  where: buildWhereClause ? buildWhereClause(filterValues) : null,
                },
              }}
            >
              {({ loading, error, data }) => {
                if (loading) return <Loading />;
                if (error) return <p>Error: {error.message}</p>;

                if (!data || !Array.isArray(data[queryName].content)) return <p>No Data</p>;

                const content = data[queryName].content;
                const transformedData = transformData ? transformData(content) : content;
                const total = data[queryName].totalElements;

                return (
                  <Fragment>
                    {enableExport && (
                      <div className='d-flex justify-content-end mb-3'>
                        <Button color='primary' onClick={() => handleExport(total)} disabled={isExporting}>
                          {isExporting ? 'Preparing Export...' : 'Download CSV'}
                        </Button>
                        <CSVLink
                          ref={csvLinkRef}
                          className='d-none'
                          filename={handleFileName()}
                          data={exportData ? getCSVData(exportData) : getCSVData(transformedData)}
                          headers={getHeaders ? getHeaders() : getDefaultHeaders()}
                        />
                      </div>
                    )}
                    <div style={{ position: 'relative' }}>
                      {isExporting && <Loading />}
                      <BootstrapTable
                        keyField={keyField}
                        classes='text-center align-middle'
                        headerClasses='th-expand'
                        wrapperClasses='table-responsive'
                        data={transformedData}
                        columns={columns}
                        bordered={false}
                        remote={{
                          filter: true,
                          pagination: true,
                          sort: true,
                        }}
                        sort={{
                          dataField: sort.field,
                          order: sort.order,
                        }}
                        pagination={paginationFactory({
                          page: pagination.page,
                          sizePerPage: pagination.size,
                          totalSize: total,
                          sizePerPageList: pageSizes.map((size) => ({ text: String(size), value: size })),
                        })}
                        onTableChange={handleTableChange}
                      />
                    </div>
                  </Fragment>
                );
              }}
            </Query>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default withApollo(QueryDataTable);
